<template>
  <v-container fluid class="club-events-gate pa-3 pa-md-6">
    <div class="d-flex align-center mb-6">
      <v-avatar color="primary" size="48" class="mr-4 elevation-2">
        <v-icon dark>mdi-calendar-star</v-icon>
      </v-avatar>
      <div>
        <h1 class="text-h5 text-md-h4 font-weight-bold mb-1">{{ $t('ce_gate_title') }}</h1>
        <p class="grey--text text--darken-1 mb-0">{{ $t('ce_gate_description') }}</p>
      </div>
    </div>

    <v-alert v-if="errorMessage" type="error" outlined dismissible @input="errorMessage = ''">{{ errorMessage }}</v-alert>

    <v-card outlined class="mb-4">
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="qrCode"
              :label="$t('ce_qr_code')"
              prepend-inner-icon="mdi-qrcode"
              outlined
              dense
              hide-details
              clearable
              @keyup.enter="searchByQr"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn block color="primary" :loading="searching" :disabled="!qrCode" @click="searchByQr">{{ $t('ce_search_event') }}</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row v-if="!selected">
      <v-col cols="12">
        <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('ce_today_events') }}</div>
        <v-progress-linear v-if="loading" indeterminate />
        <v-alert v-else-if="!events.length" type="info" outlined>{{ $t('ce_no_events_today') }}</v-alert>
      </v-col>
      <v-col v-for="e in events" :key="e.id" cols="12" sm="6" md="4">
        <v-card outlined hover @click="selectEvent(e)">
          <v-card-title class="text-subtitle-1">{{ e.name }}</v-card-title>
          <v-card-text>
            <div>{{ e.time }} · {{ e.location }}</div>
            <div>{{ $t('ce_host') }}: {{ e.host_name || '-' }}</div>
            <div>{{ $t('ce_inside') }}: {{ e.guests_inside }} / {{ e.guests_total }}</div>
            <v-chip v-if="e.payment_pending" small color="orange" dark class="mt-2">{{ $t('ce_payment_pending') }}</v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-else outlined>
      <v-card-title class="d-flex justify-space-between">
        <span>{{ selected.name }}</span>
        <v-btn text @click="clearSelection"><v-icon left>mdi-arrow-left</v-icon>{{ $t('ce_back') }}</v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="selected.payment_pending" type="warning" outlined>{{ $t('ce_payment_warning') }}</v-alert>
        <div class="mb-3">
          {{ selected.time }} · {{ selected.location }} · {{ $t('ce_host') }}: {{ selected.host_name || '-' }}
          <span v-if="selected.notes"> · {{ selected.notes }}</span>
        </div>
        <v-list>
          <v-list-item v-for="g in selected.guests" :key="g.id">
            <v-list-item-content>
              <v-list-item-title>{{ g.full_name }}</v-list-item-title>
              <v-list-item-subtitle>{{ guestStatusLabel(g.status) }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn v-if="g.status === 0" color="green" dark small :loading="busy === g.id" @click="move(g, 1)">
                <v-icon left small>mdi-login</v-icon>{{ $t('ce_check_in') }}
              </v-btn>
              <v-btn v-else-if="g.status === 1" color="orange" dark small :loading="busy === g.id" @click="move(g, 2)">
                <v-icon left small>mdi-logout</v-icon>{{ $t('ce_check_out') }}
              </v-btn>
              <v-icon v-else color="grey">mdi-check-all</v-icon>
            </v-list-item-action>
          </v-list-item>
          <v-list-item v-if="!selected.guests.length"><v-list-item-content>{{ $t('ce_no_guests') }}</v-list-item-content></v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">{{ snackbarMessage }}</v-snackbar>
  </v-container>
</template>

<script>
import { getGateTodayEvents, getGateEventByQr, setGateGuestStatus } from '@/services/clubEvents'

export default {
  name: 'ClubEventsGate',
  data: () => ({
    events: [],
    selected: null,
    qrCode: '',
    loading: false,
    searching: false,
    busy: null,
    errorMessage: '',
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success'
  }),
  created () {
    this.loadToday()
  },
  methods: {
    apiError (error) {
      const data = error && error.response && error.response.data
      const value = data && (data.error || data.detail)
      return typeof value === 'string' ? value : this.$t('ce_error')
    },
    notify (message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },
    guestStatusLabel (status) {
      return this.$t(['ce_guest_pending', 'ce_guest_inside', 'ce_guest_exited'][status])
    },
    async loadToday () {
      this.loading = true
      try {
        this.events = (await getGateTodayEvents()).data
      } catch (error) {
        this.errorMessage = this.apiError(error)
      } finally {
        this.loading = false
      }
    },
    async loadEvent (qr) {
      const response = await getGateEventByQr(qr)
      this.selected = response.data
    },
    async selectEvent (event) {
      try {
        await this.loadEvent(event.qr_code)
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      }
    },
    async searchByQr () {
      if (!this.qrCode) return
      this.searching = true
      try {
        await this.loadEvent(this.qrCode.trim())
        this.qrCode = ''
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      } finally {
        this.searching = false
      }
    },
    async move (guest, status) {
      this.busy = guest.id
      try {
        await setGateGuestStatus(guest.id, status)
        await this.loadEvent(this.selected.qr_code)
        this.notify(this.$t(status === 1 ? 'ce_check_in_ok' : 'ce_check_out_ok'))
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      } finally {
        this.busy = null
      }
    },
    clearSelection () {
      this.selected = null
      this.loadToday()
    }
  }
}
</script>
